import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import {
  Users,
  Eye,
  TrendingUp,
  Activity,
  Clock,
  AlertTriangle,
  RefreshCw,
  Zap,
  Bug,
  MousePointerClick,
  ExternalLink,
  Shield,
  Video,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface DauDataPoint {
  date: string;
  users: number;
}

interface TopPage {
  path: string;
  views: number;
}

interface UserActivity {
  email: string;
  companyId: string;
  role: string;
  lastSeen: string;
}

interface ErrorDataPoint {
  date: string;
  errors: number;
}

interface TopError {
  message: string;
  page: string;
  count: number;
}

interface RageClick {
  email: string;
  companyId: string;
  page: string;
  count: number;
}

interface CompanyHealth {
  companyId: string;
  users: number;
  events: number;
  errors: number;
  rageClicks: number;
  lastActivity: string;
  healthScore: 'healthy' | 'warning' | 'critical';
}

interface PostHogAnalyticsResponse {
  dau: number;
  wau: number;
  mau: number;
  stickiness: number;
  dauTrend: DauDataPoint[];
  topPages: TopPage[];
  activeUsers: UserActivity[];
  inactiveUsers: UserActivity[];
  errorTrend: ErrorDataPoint[];
  topErrors: TopError[];
  totalErrors: number;
  rageClicks: RageClick[];
  totalRageClicks: number;
  companyHealth: CompanyHealth[];
  posthogProjectId: string;
  period: string;
  generatedAt: string;
}

type Period = '7d' | '30d' | '90d';

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function cleanPagePath(path: string): string {
  return path.replace(/\/$/, '') || '/';
}

function truncateError(msg: string, maxLen = 80): string {
  return msg.length > maxLen ? msg.substring(0, maxLen) + '...' : msg;
}

function sessionReplayUrl(projectId: string, email: string): string {
  return `https://eu.posthog.com/project/${projectId}/replay?filter_person_email=${encodeURIComponent(email)}`;
}

const roleColors: Record<string, string> = {
  super_admin: 'bg-red-500/20 text-red-300 border-red-500/30',
  administrator: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  office_admin: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  broker: 'bg-green-500/20 text-green-300 border-green-500/30',
};

const healthBadge: Record<string, { bg: string; text: string; label: string }> = {
  healthy: { bg: 'bg-green-500/20', text: 'text-green-300', label: 'Healthy' },
  warning: { bg: 'bg-amber-500/20', text: 'text-amber-300', label: 'Warning' },
  critical: { bg: 'bg-red-500/20', text: 'text-red-300', label: 'Critical' },
};

function ReplayLink({ projectId, email }: { projectId: string; email: string }) {
  return (
    <a
      href={sessionReplayUrl(projectId, email)}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-400 hover:text-blue-300 transition-colors"
      title="Watch session replay"
    >
      <Video className="w-4 h-4" />
    </a>
  );
}

export function UserAnalyticsTab() {
  const [data, setData] = useState<PostHogAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState<Period>('30d');

  const fetchAnalytics = useCallback(async (showToast = false) => {
    try {
      if (showToast) setRefreshing(true);
      else setLoading(true);

      const getPostHogAnalytics = httpsCallable<{ period: string }, PostHogAnalyticsResponse>(
        functions,
        'getPostHogAnalytics'
      );
      const result = await getPostHogAnalytics({ period });
      setData(result.data);
      if (showToast) toast.success('Analytics refreshed');
    } catch (err: any) {
      console.error('Error fetching PostHog analytics:', err);
      toast.error(err.message || 'Failed to load user analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="text-white text-lg">Loading user analytics...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-400">Failed to load user analytics. Check PostHog configuration.</p>
      </div>
    );
  }

  const maxPageViews = data.topPages.length > 0 ? data.topPages[0].views : 1;
  const topErrors = data.topErrors || [];
  const errorTrend = data.errorTrend || [];
  const rageClicks = data.rageClicks || [];
  const companyHealth = data.companyHealth || [];
  const totalErrors = data.totalErrors || 0;
  const totalRageClicks = data.totalRageClicks || 0;
  const maxErrorCount = topErrors.length > 0 ? topErrors[0].count : 1;

  return (
    <div className="space-y-6">
      {/* Period selector + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50">
          {(['7d', '30d', '90d'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
                period === p
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
              }`}
            >
              {p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Last 90 days'}
            </button>
          ))}
        </div>
        <button
          onClick={() => fetchAnalytics(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 rounded-lg transition-all disabled:opacity-50 text-slate-300"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Engagement KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 border border-blue-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{data.dau}</div>
          <div className="text-blue-100 text-sm">Daily Active Users</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-blue-100">Today</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 border border-purple-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{data.wau}</div>
          <div className="text-purple-100 text-sm">Weekly Active (avg)</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-purple-100">Last 7 days</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-xl p-6 border border-green-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{data.mau}</div>
          <div className="text-green-100 text-sm">Monthly Active (avg)</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-green-100">Last 30 days</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl p-6 border border-orange-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Eye className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{data.stickiness}%</div>
          <div className="text-orange-100 text-sm">Stickiness (DAU/MAU)</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-orange-100">
              {data.stickiness >= 20 ? 'Healthy' : 'Needs improvement'}
            </span>
          </div>
        </div>
      </div>

      {/* Error + Rage Click KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-xl p-6 border border-red-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Bug className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{totalErrors}</div>
          <div className="text-red-100 text-sm">Total Errors</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-red-100">
              {totalErrors === 0 ? 'No errors detected' : `${topErrors.length} unique error types`}
            </span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl p-6 border border-amber-500/50">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <MousePointerClick className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="text-3xl font-bold mb-2">{totalRageClicks}</div>
          <div className="text-amber-100 text-sm">Rage Clicks</div>
          <div className="mt-2 pt-2 border-t border-white/20">
            <span className="text-sm text-amber-100">
              {totalRageClicks === 0 ? 'No frustrated users' : `${rageClicks.length} frustrated users`}
            </span>
          </div>
        </div>
      </div>

      {/* DAU Trend Chart */}
      {data.dauTrend.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Daily Active Users</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.dauTrend}>
                <defs>
                  <linearGradient id="dauGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                  }}
                  labelFormatter={(val) => new Date(val).toLocaleDateString('en-AU')}
                  formatter={(value: number) => [value, 'Users']}
                />
                <Area
                  type="monotone"
                  dataKey="users"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#dauGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Error Trend Chart */}
      {errorTrend.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Bug className="w-6 h-6 text-red-400" />
            <h2 className="text-xl font-semibold text-white">Error Trend</h2>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={errorTrend}>
                <defs>
                  <linearGradient id="errorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#94a3b8"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    color: '#f1f5f9',
                  }}
                  labelFormatter={(val) => new Date(val).toLocaleDateString('en-AU')}
                  formatter={(value: number) => [value, 'Errors']}
                />
                <Area
                  type="monotone"
                  dataKey="errors"
                  stroke="#ef4444"
                  strokeWidth={2}
                  fill="url(#errorGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Errors */}
      {topErrors.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-6 h-6 text-red-400" />
            <h2 className="text-xl font-semibold text-white">Top Errors</h2>
          </div>
          <div className="space-y-3">
            {topErrors.map((error, index) => {
              const percentage = (error.count / maxErrorCount) * 100;
              return (
                <div key={`${error.message}-${error.page}-${index}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex-1 min-w-0 mr-4">
                      <span className="text-sm text-gray-200 font-mono block truncate" title={error.message}>
                        {truncateError(error.message)}
                      </span>
                      <span className="text-xs text-gray-500 font-mono">{cleanPagePath(error.page)}</span>
                    </div>
                    <span className="text-sm font-semibold text-red-400 whitespace-nowrap">
                      {error.count}x
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div
                      className="bg-red-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Pages */}
      {data.topPages.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Eye className="w-6 h-6 text-indigo-400" />
            <h2 className="text-xl font-semibold text-white">Top Pages</h2>
          </div>
          <div className="space-y-3">
            {data.topPages.map((page, index) => {
              const percentage = (page.views / maxPageViews) * 100;
              return (
                <div key={page.path}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-gray-400 w-6">{index + 1}.</span>
                      <span className="text-sm text-gray-200 font-mono">{cleanPagePath(page.path)}</span>
                    </div>
                    <span className="text-sm font-semibold text-white">
                      {page.views.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2 ml-9">
                    <div
                      className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Frustrated Users (Rage Clicks) */}
      {rageClicks.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <MousePointerClick className="w-6 h-6 text-amber-400" />
            <h2 className="text-xl font-semibold text-white">Frustrated Users</h2>
            <span className="text-sm text-gray-400">(rage clicks)</span>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {rageClicks.map((rc, index) => (
              <div
                key={`${rc.email}-${rc.page}-${index}`}
                className="flex items-center justify-between p-3 bg-gray-900 rounded-lg border border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{rc.email}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-400 truncate">{rc.companyId}</span>
                    <span className="text-xs text-amber-400 font-mono">{cleanPagePath(rc.page)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-3">
                  <span className="text-sm font-semibold text-amber-400 whitespace-nowrap">
                    {rc.count} clicks
                  </span>
                  {data.posthogProjectId && <ReplayLink projectId={data.posthogProjectId} email={rc.email} />}

                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Customer Health */}
      {companyHealth.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-semibold text-white">Customer Health</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 font-medium py-3 pr-4">Company</th>
                  <th className="text-right text-gray-400 font-medium py-3 px-3">Users</th>
                  <th className="text-right text-gray-400 font-medium py-3 px-3">Events</th>
                  <th className="text-right text-gray-400 font-medium py-3 px-3">Errors</th>
                  <th className="text-right text-gray-400 font-medium py-3 px-3">Rage</th>
                  <th className="text-center text-gray-400 font-medium py-3 px-3">Health</th>
                  <th className="text-right text-gray-400 font-medium py-3 pl-3">Last Active</th>
                </tr>
              </thead>
              <tbody>
                {companyHealth.map((company) => {
                  const badge = healthBadge[company.healthScore];
                  return (
                    <tr key={company.companyId} className="border-b border-gray-700/50 hover:bg-gray-700/20">
                      <td className="py-3 pr-4">
                        <span className="text-white font-mono text-xs">{company.companyId}</span>
                      </td>
                      <td className="text-right py-3 px-3 text-gray-300">{company.users}</td>
                      <td className="text-right py-3 px-3 text-gray-300">{company.events.toLocaleString()}</td>
                      <td className="text-right py-3 px-3">
                        <span className={company.errors > 0 ? 'text-red-400 font-semibold' : 'text-gray-500'}>
                          {company.errors}
                        </span>
                      </td>
                      <td className="text-right py-3 px-3">
                        <span className={company.rageClicks > 0 ? 'text-amber-400 font-semibold' : 'text-gray-500'}>
                          {company.rageClicks}
                        </span>
                      </td>
                      <td className="text-center py-3 px-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="text-right py-3 pl-3 text-gray-400 text-xs">
                        {formatRelativeTime(company.lastActivity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* User Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recently Active */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-6 h-6 text-green-400" />
            <h2 className="text-lg font-semibold text-white">Recently Active Users</h2>
            <span className="text-sm text-gray-400">({data.activeUsers.length})</span>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {data.activeUsers.length === 0 ? (
              <p className="text-gray-500 text-sm">No active users in this period</p>
            ) : (
              data.activeUsers.map((user) => (
                <div
                  key={user.email}
                  className="flex items-center justify-between p-3 bg-gray-900 rounded-lg border border-gray-700"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{user.email}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400 truncate">{user.companyId}</span>
                      {user.role && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full border ${
                            roleColors[user.role] || 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                          }`}
                        >
                          {user.role.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="text-xs text-green-400 whitespace-nowrap">
                      {formatRelativeTime(user.lastSeen)}
                    </span>
                    {data.posthogProjectId && <ReplayLink projectId={data.posthogProjectId} email={user.email} />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Inactive Users */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Inactive Users (14+ days)</h2>
            <span className="text-sm text-gray-400">({data.inactiveUsers.length})</span>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {data.inactiveUsers.length === 0 ? (
              <p className="text-green-400 text-sm">No inactive users detected</p>
            ) : (
              data.inactiveUsers.map((user) => {
                const daysSince = Math.floor(
                  (Date.now() - new Date(user.lastSeen).getTime()) / 86400000
                );
                const severity = daysSince >= 30 ? 'text-red-400' : 'text-amber-400';
                return (
                  <div
                    key={user.email}
                    className="flex items-center justify-between p-3 bg-gray-900 rounded-lg border border-gray-700"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{user.email}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400 truncate">{user.companyId}</span>
                        {user.role && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full border ${
                              roleColors[user.role] || 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                            }`}
                          >
                            {user.role.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <span className={`text-xs whitespace-nowrap font-semibold ${severity}`}>
                        {daysSince}d inactive
                      </span>
                      {data.posthogProjectId && <ReplayLink projectId={data.posthogProjectId} email={user.email} />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500">
        Data from PostHog &middot; Generated {new Date(data.generatedAt).toLocaleString('en-AU')}
      </div>
    </div>
  );
}
